import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useMe, useNames, usePeople, useSendNoteDm, useSlack } from '../api/queries';
import { slackMineUrl } from '../pages/SlackPost';
import { Button, Empty, MenuButton, Modal, SkeletonRows, useNotices } from '../ui';

/**
 * The note, or one write-up, to one person's Slack — from your own Slack, in
 * the conversation the two of you already have (2026-10-08). Aleksandr: notes
 * straight into a direct message, not through the Ptitim bot. So there is no
 * "from the bot" here at all.
 */
export function SendNoteDm({
  meetingId,
  attendees,
  writeup,
  onClose,
}: {
  meetingId: string;
  attendees: string[];
  /** One of the meeting's write-ups; absent: the full note. */
  writeup?: { id: string; templateName: string };
  onClose: () => void;
}): ReactElement {
  const slack = useSlack();
  const people = usePeople();
  const names = useNames();
  const me = useMe();
  const send = useSendNoteDm(meetingId);
  const [to, setTo] = useState<string | null>(null);
  const { show } = useNotices();

  // The meeting's own people first, then everybody else you have met.
  const candidates = useMemo(() => {
    const seen = new Set<string>([me.data?.email.toLowerCase() ?? '']);
    const rows: Array<{ email: string; here: boolean }> = [];
    const add = (email: string, here: boolean) => {
      const address = email.toLowerCase();
      if (seen.has(address)) return;
      seen.add(address);
      rows.push({ email: address, here });
    };
    for (const email of attendees) add(email, true);
    for (const p of people.data ?? []) add(p.email, false);
    return rows;
  }, [attendees, people.data, me.data]);
  const label = (email: string) => names.get(email) ?? email;

  const ready = slack.data?.connected === true && slack.data.me.connected;
  const what = writeup ? `“${writeup.templateName}”` : 'The full note';
  return (
    <Modal
      title="Send to a person on Slack"
      onClose={onClose}
      foot={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready || !to}
            working={send.isPending ? 'Sending…' : false}
            onClick={() => {
              if (!to) return;
              send.mutate(
                { to, ...(writeup ? { writeupId: writeup.id } : {}) },
                {
                  onSuccess: (sent) => {
                    show({ tone: 'success', text: `Sent to ${sent.name}, from you.` });
                    onClose();
                  },
                  onError: (e) => show({ tone: 'failure', text: e.message }),
                },
              );
            }}
          >
            Send
          </Button>
        </>
      }
    >
      {slack.isPending ? (
        <SkeletonRows rows={2} />
      ) : !slack.data?.connected ? (
        <Empty lead="Slack is not connected to this workspace.">
          An administrator connects it once, under Settings → Connected apps.
        </Empty>
      ) : !slack.data.me.connected ? (
        <Empty lead="Connect your own Slack first.">
          It comes from you, in your own direct messages:{' '}
          <a href={slackMineUrl}>Connect my Slack</a>. It asks for sending as you and opening a DM —
          nothing that reads.
        </Empty>
      ) : (
        <div className="stack">
          <p className="t-secondary">
            {what} goes to them from you, in your own direct messages — never through the Ptitim bot
            — with a link back to the meeting. They can read it whether or not they use Ptitim.
          </p>
          <MenuButton
            label={to ? label(to) : 'Choose a person'}
            align="left"
            search
            items={candidates.map((c) => ({
              id: c.email,
              label: label(c.email),
              sub: c.here ? `${c.email} · in this meeting` : c.email,
              current: c.email === to,
              onSelect: () => setTo(c.email),
            }))}
          />
        </div>
      )}
    </Modal>
  );
}
